import { useState, useMemo, useCallback, ReactNode } from 'react'
import { X, Plus, CircleXIcon } from 'lucide-react'
import { toast } from 'react-toastify'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { MobileColors } from 'styles/mobileTokens'
import FbButton from 'components/FbUI/FbButton'
import { Form } from 'react-bootstrap'
import { MobileModal } from 'components/UI/Modal/MobileModal'
import { SortSelect } from 'components/SortSelect'
import { SortingOptionLabel } from 'components/SortSelect/SortingOptionLabel'
import Checkbox from 'components/Checkbox/checkbox'
import { ManageTagsDistributorsParams } from 'services/common_table_actions'

type SortOption =
  | 'modified'
  | '-modified'
  | 'created'
  | '-created'
  | 'name'
  | '-name'

const defaultSortOptions = [
  {
    value: 'modified',
    label: <SortingOptionLabel label="Last Modified" type="number" />,
  },
  {
    value: '-modified',
    label: <SortingOptionLabel label="Last Modified" type="number" desc />,
  },
  {
    value: 'created',
    label: <SortingOptionLabel label="Date Created" type="number" />,
  },
  {
    value: '-created',
    label: <SortingOptionLabel label="Date Created" type="number" desc />,
  },
  { value: 'name', label: <SortingOptionLabel label="Name" /> },
  { value: '-name', label: <SortingOptionLabel label="Name" desc /> },
]

export interface MobileAssignModalProps<T extends { id: number }> {
  isOpen: boolean
  onClose: () => void
  title: string
  subtitle?: string
  items: T[]
  renderItem: (item: T) => ReactNode
  renderCount?: (item: T) => ReactNode
  countHeader?: string
  apiAction: (params: ManageTagsDistributorsParams) => Promise<any>
  itemListKey?: string
  onSuccess?: () => void
  refetchFn?: () => Promise<any>
  // For creating new items
  onCreateNew?: (value: string) => Promise<T | null>
  createNewLabel?: string
  createNewPlaceholder?: string
  // Sorting
  sortOptions?: typeof defaultSortOptions
  getSortValue?: (item: T, sortBy: SortOption) => string | number | Date
  assignType?: string
}

export function MobileAssignModal<T extends { id: number }>({
  isOpen,
  onClose,
  title,
  subtitle = 'Select tags to apply or remove.',
  items,
  renderItem,
  renderCount,
  countHeader = 'Count',
  apiAction,
  itemListKey = 'taglist',
  onSuccess,
  refetchFn,
  onCreateNew,
  createNewLabel = 'Create New',
  createNewPlaceholder = 'New item name',
  sortOptions = defaultSortOptions,
  getSortValue,
  assignType = 'tags',
}: MobileAssignModalProps<T>) {
  const queryClient = useQueryClient()

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const initialSort: SortOption =
    assignType === 'tags' ? '-modified' : '-created'
  const [sortBy, setSortBy] = useState<SortOption>(initialSort)
  const [isCreating, setIsCreating] = useState(false)
  const [newItemName, setNewItemName] = useState('')

  // Sort items based on selected option
  const sortedItems = useMemo(() => {
    if (!items) return []
    if (!getSortValue) return items

    const sorted = [...items]
    const isDesc = sortBy.startsWith('-')
    const field = isDesc ? sortBy.slice(1) : sortBy

    return sorted.sort((a, b) => {
      const aVal = getSortValue(a, field as SortOption)
      const bVal = getSortValue(b, field as SortOption)

      let comparison = 0
      if (aVal instanceof Date && bVal instanceof Date) {
        comparison = aVal.getTime() - bVal.getTime()
      } else if (typeof aVal === 'string' && typeof bVal === 'string') {
        comparison = aVal.localeCompare(bVal)
      } else {
        comparison = (aVal as number) - (bVal as number)
      }

      return isDesc ? -comparison : comparison
    })
  }, [items, sortBy, getSortValue])

  const handleUnselect = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev)
      newSet.delete(id)
      return newSet
    })
  }, [])

  // Mutation for applying/removing items
  const mutation = useMutation({
    mutationFn: async ({
      action,
      ids,
    }: {
      action: 'add' | 'delete'
      ids: number[]
    }) => {
      const params: any = {
        action,
        items: ids,
        [itemListKey]: ids,
      }
      return apiAction(params)
    },
    onSuccess: (data) => {
      if (data.status === 202) {
        toast.info(data.data?.message ?? 'Processing your request...')
      } else {
        toast.success(data.data?.message ?? 'Updated successfully')
      }
      queryClient.invalidateQueries()
      refetchFn?.()
      onSuccess?.()
      handleClose()
    },
    onError: (err) => {
      toast.error(`Error updating: ${err}`)
    },
  })

  const handleToggle = (id: number) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const handleApply = () => {
    if (selectedIds.size === 0) return
    mutation.mutate({ action: 'add', ids: Array.from(selectedIds) })
  }

  const handleDelete = () => {
    if (selectedIds.size === 0) return
    mutation.mutate({ action: 'delete', ids: Array.from(selectedIds) })
  }

  const handleCreateNew = useCallback(async () => {
    if (!newItemName.trim() || !onCreateNew) return
    if (newItemName.length > 50) {
      toast.error('Name is limited to 50 characters.')
      return
    }
    try {
      const newItem = await onCreateNew(newItemName.trim())
      if (newItem) {
        toast.success('Created successfully')
        setNewItemName('')
        setIsCreating(false)
        refetchFn?.()
      }
    } catch (e) {
      toast.error(`Error creating: ${e}`)
    }
  }, [newItemName, onCreateNew, refetchFn])

  const handleClose = () => {
    setSelectedIds(new Set())
    setIsCreating(false)
    setNewItemName('')
    onClose()
  }

  if (!isOpen) return null

  return (
    <MobileModal
      handleBack={handleClose}
      backLabel="Cancel"
      backIcon={<CircleXIcon size={20} />}
      header={
        <div className="p-2">
          <h1 className="text-xl font-semibold text-gray-900 mb-1">{title}</h1>
          <p className="text-sm text-gray-500">{subtitle}</p>
        </div>
      }
      footer={
        <div>
          {selectedIds.size > 0 && (
            <div className="flex flex-wrap px-2 mb-2 gap-2">
              {Array.from(selectedIds).map((id) => {
                const item = items.find((i) => i.id === id)
                return item ? (
                  <span
                    key={id}
                    className="inline-flex items-center px-1 rounded-full text-xs max-h-7"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      backgroundColor: (item as any).color ?? '#dbdbdbad',
                    }}
                  >
                    <span className="truncate flex-1">{renderItem(item)}</span>
                    <button
                      type="button"
                      onClick={() => handleUnselect(id)}
                      className="rounded-full"
                    >
                      <X size={12} />
                    </button>
                  </span>
                ) : null
              })}
            </div>
          )}
          <div className="flex gap-2 px-2 p-2 justify-between">
            <div className="w-1/2">
              <FbButton
                className="w-full"
                type="button"
                size="mdlg"
                variant="danger-outline"
                onClick={handleDelete}
                disabled={selectedIds.size === 0 || mutation.isPending}
              >
                Delete
              </FbButton>
            </div>
            <div className="w-1/2">
              <FbButton
                className="w-full"
                type="button"
                size="mdlg"
                variant="primary"
                onClick={handleApply}
                disabled={selectedIds.size === 0 || mutation.isPending}
              >
                {mutation.isPending ? 'Applying...' : 'Apply'}
              </FbButton>
            </div>
          </div>
        </div>
      }
    >
      {/* Sort and Create Row */}
      {!isCreating && (
        <div
          className={`flex items-center justify-between py-2 px-3 border-gray-200 ${assignType ? '' : 'justify-end'}`}
        >
          {assignType !== 'tags' ? (
            /* Distributors / GPOs: Full-width single sort dropdown */
            <div className="w-full max-w-sm">
              <SortSelect
                value={sortOptions.find((opt) => opt.value === sortBy)}
                onChange={(newValue: any) =>
                  setSortBy(newValue?.value as SortOption)
                }
                options={[
                  { value: 'name', label: <SortingOptionLabel label="Name" /> },
                  {
                    value: '-name',
                    label: <SortingOptionLabel label="Name" desc />,
                  },
                  {
                    value: 'created',
                    label: (
                      <SortingOptionLabel label="Date Created" type="number" />
                    ),
                  },
                  {
                    value: '-created',
                    label: (
                      <SortingOptionLabel
                        label="Date Created"
                        type="number"
                        desc
                      />
                    ),
                  },
                ]}
              />
            </div>
          ) : (
            /* Tags: Compact sort + create button */
            <>
              <div className="w-48">
                <SortSelect
                  value={sortOptions.find((opt) => opt.value === sortBy)}
                  onChange={(newValue: any) =>
                    setSortBy(newValue?.value as SortOption)
                  }
                  options={sortOptions}
                />
              </div>
              {onCreateNew && (
                <button
                  onClick={() => setIsCreating(true)}
                  className="flex items-center gap-1 text-sm font-medium bg-transparent border-none cursor-pointer"
                  style={{ color: MobileColors.primary }}
                >
                  <Plus size={16} />
                  {createNewLabel}
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Create New Input */}
      {isCreating && onCreateNew && (
        <div className="flex items-center justify-between py-2 px-3 border-gray-200 gap-2">
          <Form.Control
            type="text"
            placeholder={createNewPlaceholder}
            value={newItemName}
            maxLength={50}
            onChange={(e) => setNewItemName(e.target.value)}
            className="flex-1 text-sm py-2.5"
            autoFocus
          />
          <div className="flex border border-gray-200 rounded-md overflow-hidden">
            <FbButton
              variant="secondary"
              size="sm"
              className="!p-2.5 !h-auto !min-h-0 flex-shrink-0 !rounded-none"
              onClick={() => {
                setIsCreating(false)
                setNewItemName('')
              }}
            >
              <X size={18} />
            </FbButton>
            <FbButton
              variant="primary"
              size="sm"
              className="!p-2.5 !h-auto !min-h-0 flex-shrink-0 !bg-green-700 !rounded-none"
              onClick={handleCreateNew}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </FbButton>
          </div>
        </div>
      )}

      <div className="px-3">
        {/* Item Count */}
        <div className="py-2 text-xs text-gray-500">
          {sortedItems.length} {assignType === 'tags' ? 'tags' : 'distributors'}
          :
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="border border-gray-200 overflow-hidden bg-white">
            {/* Table Header */}
            <div className="flex items-center py-2 px-2 border-b bg-gray-50 border-gray-200 text-sm font-medium text-gray-600">
              <div className="w-8 flex items-center justify-center">
                <Checkbox
                  checked={false}
                  onChange={() => {}}
                  wrapperClassName=""
                  readOnly
                />
              </div>
              <div className="flex-1">Name</div>
              {renderCount && (
                <div className="w-20 text-right">{countHeader}</div>
              )}
            </div>

            {/* Item Rows */}
            {sortedItems.map((item) => {
              const isSelected = selectedIds.has(item.id)
              return (
                <div
                  key={item.id}
                  className={`flex items-center py-2 px-3 border-b border-gray-200 last:border-b-0 cursor-pointer
                  ${isSelected ? 'bg-gray-50' : ''}`}
                  onClick={() => handleToggle(item.id)}
                >
                  <Checkbox
                    checked={selectedIds.has(item.id)}
                    onChange={() => {}}
                    onClick={(e) => e.stopPropagation()}
                    wrapperClassName="mr-2"
                    readOnly
                  />
                  <div className="flex-1">{renderItem(item)}</div>
                  {renderCount && (
                    <div className="w-20 text-right text-sm text-gray-600">
                      {renderCount(item)}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </MobileModal>
  )
}
